import { useId, useState } from 'react';
import { useClientList } from '../../api/queries.ts';
import { ApiError } from '../../api/client.ts';
import { formatDate } from '../../dates.ts';
import { field, hint, label } from '../contacts/Sheet.tsx';
import { StatusTag } from '../StatusTag.tsx';
import {
  DUE_PRESETS, QUICK_MINUTES, dueSpecFrom, duePreview, localValue, timeState, whenState, type DueChoice,
} from '../../tasks/fields.ts';
import type { DueSpec } from '../../dueDates.ts';

/**
 * The fields every task form shares (147, A-#77): time spent (ruling 10), a due date (ruling 11), when it happened
 * (ruling 8) and a client the reader can see. The logic is tasks/fields.ts; the reading of time and dates is the shared
 * modules'. Every control is 44 px or more, and colours are tokens.
 */

/** A primary button in the brand's colours, which dark mode redefines (never white on a neutral that turns light). */
export const go = 'min-h-[44px] rounded-lg bg-brand px-4 text-sm font-semibold text-on-brand disabled:bg-neutral-300 disabled:text-neutral-700 disabled:cursor-not-allowed';
export const plain = 'min-h-[44px] rounded-lg border border-neutral-300 bg-surface px-3 text-sm font-semibold text-neutral-900 hover:bg-neutral-50';
export const chip = (on: boolean) =>
  `min-h-[44px] min-w-[44px] rounded-lg border px-3 text-sm font-semibold ${on ? 'border-brand bg-brand text-on-brand' : 'border-neutral-300 bg-surface text-neutral-900'}`;

/** A refusal's sentence, as the server wrote it (the contract: fit to show as it is). */
export function Refusal({ error }: { error: unknown }) {
  if (!error) return null;
  const text = error instanceof ApiError ? error.message : 'That could not be saved. Try again.';
  const more = error instanceof ApiError ? error.hint : undefined;
  return (
    <p role="alert" className="mt-2 font-semibold text-critical-edge">
      {text}{more ? <span className="block text-sm font-normal">{more}</span> : null}
    </p>
  );
}

export function TimeField({ value, onChange }: { value: string; onChange: (text: string) => void }) {
  const id = useId();
  const s = timeState(value);
  return (
    <div>
      <label className={label} htmlFor={id}>Time spent</label>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <input id={id} type="text" inputMode="text" autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)}
               placeholder="90, 1h30m or 1.5h" aria-describedby={`${id}-echo`} className={`${field} mt-0 w-40`} />
        {QUICK_MINUTES.map((m) => (
          <button key={m} type="button" className={chip(value === String(m))} onClick={() => onChange(String(m))}>
            {m < 60 ? `${m} min` : `${m / 60} h`}
          </button>
        ))}
      </div>
      <p id={`${id}-echo`} role="status" className={'error' in s ? 'mt-1 text-sm font-semibold text-critical-edge' : hint}>
        {'empty' in s ? 'Whole minutes: 90, 90m, 2h, 0.5h, 1h30m or 1.5h.' : 'error' in s ? s.error : `Understood as ${s.echo}.`}
      </p>
    </div>
  );
}

export interface DueValue { choice: DueChoice; n: string; date: string }
export const DUE_NONE: DueValue = { choice: 'none', n: '', date: '' };

export function DueField({ value, onChange, today, allowNone = true }: {
  value: DueValue; onChange: (v: DueValue) => void; today: string; allowNone?: boolean;
}) {
  const id = useId();
  const r = dueSpecFrom(value.choice, value.n, value.date, today);
  const preview = 'spec' in r ? duePreview(r.spec, today) : null;
  const set = (p: Partial<DueValue>) => onChange({ ...value, ...p });
  return (
    <fieldset>
      <legend className={label}>Due</legend>
      <div className="mt-1 flex flex-wrap gap-2">
        {DUE_PRESETS.map(([k, words]) => (
          <button key={k} type="button" className={chip(value.choice === k)} onClick={() => set({ choice: k })}>{words}</button>
        ))}
        {allowNone && <button type="button" className={chip(value.choice === 'none')} onClick={() => set({ choice: 'none' })}>No due date</button>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" className={chip(value.choice === 'in_days')} onClick={() => set({ choice: 'in_days' })}>In N days</button>
        <button type="button" className={chip(value.choice === 'in_business_days')} onClick={() => set({ choice: 'in_business_days' })}>In N business days</button>
        {(value.choice === 'in_days' || value.choice === 'in_business_days') && (
          <label className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
            N
            <input type="text" inputMode="numeric" value={value.n} onChange={(e) => set({ n: e.target.value })} className={`${field} mt-0 w-20`} />
          </label>
        )}
        <button type="button" className={chip(value.choice === 'date')} onClick={() => set({ choice: 'date' })}>Pick a date</button>
        {value.choice === 'date' && (
          <input type="date" min={today} value={value.date} aria-label="Due date" onChange={(e) => set({ date: e.target.value })} className={`${field} mt-0 w-48`} />
        )}
      </div>
      <p id={`${id}-due`} role="status" className={'error' in r ? 'mt-1 text-sm font-semibold text-critical-edge' : hint}>
        {'error' in r ? r.error : preview ? `Due ${formatDate(preview)}.` : 'No due date.'}
      </p>
    </fieldset>
  );
}

/** The spec to send, or null when the field is not yet valid (the caller then keeps the button off). */
export function dueToSend(v: DueValue, today: string): { spec: DueSpec } | null {
  const r = dueSpecFrom(v.choice, v.n, v.date, today);
  return 'spec' in r ? r : null;
}

export function WhenField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const id = useId();
  const s = whenState(value, new Date());
  return (
    <div>
      <label className={label} htmlFor={id}>When it happened</label>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <input id={id} type="datetime-local" value={value} max={localValue(new Date())} onChange={(e) => onChange(e.target.value)}
               className={`${field} mt-0 w-64`} />
        <button type="button" className={plain} onClick={() => onChange(localValue(new Date()))}>Now</button>
      </div>
      <p role="status" className={'error' in s ? 'mt-1 text-sm font-semibold text-critical-edge' : hint}>
        {'error' in s ? s.error : 'Now, or earlier. The time it is recorded is kept as well.'}
      </p>
    </div>
  );
}

/**
 * A client the reader can see: the client search's own list (a POST, nothing in the address), so row security decides
 * who is offered. The typed text stays in this field.
 */
export function ClientChooser({ chosen, onChoose }: {
  chosen: { id: string; name: string; ref: string | null } | null;
  onChoose: (c: { id: string; name: string; ref: string | null } | null) => void;
}) {
  const id = useId();
  const [q, setQ] = useState('');
  const list = useClientList({ q: q.trim(), sort: 'last_name', offset: 0, limit: 8, group: 'Active' });
  if (chosen) {
    return (
      <div>
        <p className={label}>Client</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="font-semibold text-neutral-900">{chosen.name}</span>
          <span className="font-mono text-sm text-neutral-700">{chosen.ref}</span>
          <button type="button" className={plain} onClick={() => onChoose(null)}>Change</button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <label className={label} htmlFor={id}>Client</label>
      <input id={id} type="search" autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or reference" className={field} />
      {q.trim().length >= 2 && (
        <ul className="mt-1 divide-y divide-neutral-200 rounded-lg border border-neutral-300">
          {list.isPending ? <li className="px-3 py-2 text-neutral-700">Searching…</li>
            : (list.data?.clients ?? []).length === 0 ? <li className="px-3 py-2 text-neutral-700">No client you can see matches.</li>
            : list.data!.clients.map((c) => (
              <li key={c.client_id}>
                <button type="button" className="flex min-h-[44px] w-full items-center gap-2 px-3 text-left hover:bg-neutral-50"
                        onClick={() => onChoose({ id: c.client_id, name: c.display_name, ref: c.client_ref })}>
                  <span className="min-w-0 flex-1 text-neutral-900">{c.display_name}</span>
                  <StatusTag status={c.status} />
                  <span className="font-mono text-sm text-neutral-700">{c.client_ref}</span>
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
